import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Select,
  MenuItem,
  Divider,
  useTheme,
  Switch,
  FormControlLabel
} from "@mui/material";
import TranslateIcon from "@mui/icons-material/Translate";
import { useParams } from "react-router-dom";
import axios from "axios";
import dayjs from "dayjs";

const ProfileMedalsExtern = () => {
  const { id } = useParams();
  const theme = useTheme();
  const [language, setLanguage] = useState("de");
  const [catalog, setCatalog] = useState([]);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState("current");
  const [compareMonth, setCompareMonth] = useState("");
  const [values, setValues] = useState({});
  const [compareValues, setCompareValues] = useState({});
  const [username, setUsername] = useState("");

  const currentMonth = dayjs().format("YYYY-MM");
  const isCurrentMonthSelected = selectedMonth === "current";
  const showOnlyCurrent = isCurrentMonthSelected && !compareMonth;
  const showOnlySelected = !isCurrentMonthSelected && !compareMonth;
  const showComparison = compareMonth !== "";

  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem("token");
      try {
        const [userRes, catalogRes, monthsRes] = await Promise.all([
          axios.get(`/api/users/extern/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get("/api/users/medals/catalog"),
          axios.get(`/api/users/medals/extern/${id}/months`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        setUsername(userRes.data?.username || "");
        setCatalog(catalogRes.data || []);
        setAvailableMonths(monthsRes.data || []);
      } catch (error) {
        console.error("Fehler beim Laden:", error);
      }
    };
    fetchData();
  }, [id]);

  useEffect(() => {
    const fetchMedals = async () => {
      const token = localStorage.getItem("token");
      const effectiveMonth = selectedMonth === "current" ? currentMonth : selectedMonth;

      try {
        const res = await axios.get(
          `/api/users/medals/extern/${id}/by-month?month=${effectiveMonth}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setValues(res.data?.medal_values || {});
      } catch (error) {
        console.error("Fehler beim Laden der Medaillen:", error);
      }
    };
    fetchMedals();
  }, [id, selectedMonth, currentMonth]);

  useEffect(() => {
    const fetchComparison = async () => {
      if (!compareMonth) return setCompareValues({});
      const token = localStorage.getItem("token");

      try {
        const res = await axios.get(
          `/api/users/medals/extern/${id}/by-month?month=${compareMonth}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setCompareValues(res.data?.medal_values || {});
      } catch (error) {
        console.error("Fehler beim Vergleichsmonat:", error);
      }
    };
    fetchComparison();
  }, [id, compareMonth]);

  return (
    <Box mx={2} my={4}>
      <Typography variant="h4" sx={{ mb: 2 }}>
        Medaillen von {username}
      </Typography>

      <Box
        display="flex"
        flexWrap="wrap"
        justifyContent="space-between"
        alignItems="center"
        gap={2}
        mb={3}
      >
        <FormControlLabel
          control={
            <Switch
              checked={language === "en"}
              onChange={() => setLanguage((prev) => (prev === "de" ? "en" : "de"))}
            />
          }
          label={<TranslateIcon />}
        />
      </Box>

      <Box display="flex" gap={2} mb={3} flexWrap="wrap">
        <Select fullWidth value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)}>
          <MenuItem value="current">Aktueller Monat</MenuItem>
          {availableMonths.map((month) => (
            <MenuItem key={month} value={month}>{month}</MenuItem>
          ))}
        </Select>
        <Select
          fullWidth
          value={compareMonth}
          onChange={(e) => setCompareMonth(e.target.value)}
          displayEmpty
          renderValue={(selected) =>
            selected === "" ? (
              <span style={{ color: theme.palette.text.primary }}>Kein Vergleich</span>
            ) : (
              selected
            )
          }
        >
          <MenuItem value="">
            <em>Kein Vergleich</em>
          </MenuItem>
          {availableMonths.map((month) => (
            <MenuItem key={month} value={month}>{month}</MenuItem>
          ))}
        </Select>
      </Box>

      <Divider sx={{ mb: 3 }} />

      {catalog
        .filter(
          (medal) =>
            (showOnlyCurrent && true) ||
            (showOnlySelected && values[medal.id]) ||
            (showComparison && (values[medal.id] || compareValues[medal.id]))
        )
        .map((medal) => (
          <Box
            key={medal.id}
            mb={3}
            p={2}
            sx={{
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <Typography variant="h6">
              {language === "de" ? medal.title_de : medal.title_en}
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={1}>
              {language === "de" ? medal.description_de : medal.description_en}
            </Typography>

            {showOnlyCurrent && (
              <Typography>
                {currentMonth}: <strong>{values[medal.id] || 0}</strong>
              </Typography>
            )}
            {showOnlySelected && (
              <Typography>
                {selectedMonth}: <strong>{values[medal.id] || 0}</strong>
              </Typography>
            )}
            {showComparison && (
              <Box display="flex" justifyContent="space-between">
                <Typography>
                  {selectedMonth === "current" ? currentMonth : selectedMonth}:{" "}
                  <strong>{values[medal.id] || 0}</strong>
                </Typography>
                <Typography>
                  {compareMonth}: <strong>{compareValues[medal.id] || 0}</strong>
                </Typography>
              </Box>
            )}
          </Box>
        ))}
    </Box>
  );
};

export default ProfileMedalsExtern;
